/*
 * «Ingen stol står igjen alene» — no single remainder.
 *
 * Markup + prices come from snippets/no-single-remainder.liquid. This file only
 * decides which piece to show:
 *
 * PDP  — total = stepper qty + units already in the cart.
 *        total == S-1 → the choice card («Ta alle S» / «Ta S-2 i stedet»), and
 *                       "Legg i handlekurv" points the customer to the card
 *                       instead of submitting (never a dead, disabled button).
 *        total == S   → the savings line (when there is a discount).
 *        otherwise    → the quiet hint.
 * Cart — the notice is server-rendered on the S-1 line; its buttons run the
 *        theme's normal cart change flow. Checkout is never blocked.
 *
 * Preview: roots marked `data-nsr-preview` (only emitted on unpublished themes)
 * stay hidden unless the tab opened a URL with `?nsr_preview=1`.
 */
(function () {
  if (window.NoSingleRemainder) return;

  var PREVIEW_KEY = 'nsr_preview';

  function previewEnabled() {
    var flag = null;
    try {
      flag = new URLSearchParams(window.location.search).get(PREVIEW_KEY);
    } catch (e) {}
    try {
      if (flag === '1') sessionStorage.setItem(PREVIEW_KEY, '1');
      if (flag === '0') sessionStorage.removeItem(PREVIEW_KEY);
      if (flag === null) flag = sessionStorage.getItem(PREVIEW_KEY);
    } catch (e) {}
    return flag === '1';
  }

  function int(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------------- PDP */

  function ProductRule(root) {
    this.root = root;
    this.sectionId = root.dataset.nsrSection;
    this.stock = int(root.dataset.nsrStock, 0);
    this.input = document.getElementById('Quantity-' + this.sectionId);
    this.hint = root.querySelector('[data-nsr-hint]');
    this.saving = root.querySelector('[data-nsr-saving]');
    this.card = root.querySelector('[data-nsr-card]');
    this.pendingSubmit = false;
    if (!this.input || !this.card || this.stock < 2) return;

    var form = this.input.form || document.getElementById(this.input.getAttribute('form'));
    this.form = form;
    this.submitButton = form ? form.querySelector('[type="submit"][name="add"]') : null;

    this.onQuantityChange = this.update.bind(this);
    this.input.addEventListener('change', this.onQuantityChange);
    this.input.addEventListener('input', this.onQuantityChange);

    var self = this;
    root.querySelectorAll('[data-nsr-take]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.choose(int(button.dataset.nsrTake, 0));
      });
    });

    this.applyDefaultQuantity();
    this.update();
  }

  ProductRule.prototype.cartQty = function () {
    // product-info refreshes data-cart-quantity after cart updates.
    return int(this.input.dataset.cartQuantity, int(this.root.dataset.nsrCartQty, 0));
  };

  ProductRule.prototype.total = function () {
    return int(this.input.value, 0) + this.cartQty();
  };

  // A pair (S=2) can only be bought whole, so start the stepper at 2 rather
  // than greeting the customer with the choice card.
  ProductRule.prototype.applyDefaultQuantity = function () {
    if (this.stock === 2 && this.cartQty() === 0 && int(this.input.value, 0) === 1) {
      this.setQuantity(2);
    }
  };

  ProductRule.prototype.isBlocked = function () {
    return this.total() === this.stock - 1;
  };

  ProductRule.prototype.update = function () {
    var total = this.total();
    var blocked = total === this.stock - 1;
    var complete = total === this.stock;
    var cartQty = this.cartQty();

    // A choice is only offered if it is reachable from here (the PDP can add, not remove).
    this.card.querySelectorAll('[data-nsr-take]').forEach(function (button) {
      button.hidden = int(button.dataset.nsrTake, 0) - cartQty < 1;
    });

    var wasOpen = !this.card.hidden;
    this.card.hidden = !blocked;
    if (blocked && !wasOpen) this.reveal(false);
    if (!blocked) this.card.classList.remove('nsr__card--open', 'nsr__card--nudge');

    if (this.saving) this.saving.hidden = !complete;
    if (this.hint) this.hint.hidden = blocked || (complete && !!this.saving);

    if (this.submitButton && this.card.id) {
      if (blocked) this.submitButton.setAttribute('aria-describedby', this.card.id);
      else if (this.submitButton.getAttribute('aria-describedby') === this.card.id) {
        this.submitButton.removeAttribute('aria-describedby');
      }
    }
  };

  ProductRule.prototype.reveal = function (focus) {
    var card = this.card;
    card.classList.remove('nsr__card--open');
    // Restart the entry animation.
    void card.offsetWidth;
    card.classList.add('nsr__card--open');
    if (!focus) return;
    card.classList.remove('nsr__card--nudge');
    void card.offsetWidth;
    card.classList.add('nsr__card--nudge');
    card.focus({ preventScroll: true });
    var rect = card.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      card.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }
  };

  ProductRule.prototype.setQuantity = function (quantity) {
    this.input.value = quantity;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  ProductRule.prototype.choose = function (targetTotal) {
    var quantity = targetTotal - this.cartQty();
    if (quantity < 1) return;
    var submit = this.pendingSubmit;
    this.pendingSubmit = false;
    this.setQuantity(quantity);
    if (!this.submitButton) return;
    if (submit && this.form && typeof this.form.requestSubmit === 'function') {
      // They already pressed "Legg i handlekurv" — finish what they started.
      this.form.requestSubmit(this.submitButton);
    } else {
      this.submitButton.focus();
    }
  };

  // Called (capture phase) before product-form's own submit handler.
  ProductRule.prototype.interceptSubmit = function (event) {
    if (!this.isBlocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.pendingSubmit = true;
    this.card.hidden = false;
    this.reveal(true);
  };

  /* --------------------------------------------------------------- Cart */

  function onCartChoice(event) {
    var button = event.target.closest('.nsr--cart [data-nsr-take]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    var root = button.closest('[data-nsr-root]');
    var cartItems = document.querySelector('cart-items');
    if (!root || !cartItems || typeof cartItems.updateQuantity !== 'function') return;

    var line = int(root.dataset.nsrLine, 0);
    var lineQty = int(root.dataset.nsrLineQty, 0);
    var inCart = int(root.dataset.nsrInCart, 0);
    var target = int(button.dataset.nsrTake, 0);
    var nextLineQty = lineQty + (target - inCart);
    if (!line || nextLineQty < 0) return;

    root.querySelectorAll('[data-nsr-take]').forEach(function (b) {
      b.setAttribute('aria-disabled', 'true');
    });
    button.classList.add('nsr__choice--busy');
    cartItems.updateQuantity(line, nextLineQty, 'updates[]', root.dataset.nsrVariantId);
  }

  /* --------------------------------------------------------------- Boot */

  var rules = new Map();

  function init(scope) {
    var preview = previewEnabled();
    (scope || document).querySelectorAll('[data-nsr-root]').forEach(function (root) {
      if (root.hasAttribute('data-nsr-preview')) {
        if (!preview) return;
        root.hidden = false;
      }
      if (root.dataset.nsrMode === 'product' && !rules.has(root)) {
        rules.set(root, new ProductRule(root));
      }
    });
  }

  function ruleForForm(form) {
    var found = null;
    rules.forEach(function (rule, root) {
      if (!document.contains(root)) {
        rules.delete(root);
        return;
      }
      if (rule.form === form) found = rule;
    });
    return found;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var rule = ruleForForm(event.target);
      if (rule) rule.interceptSubmit(event);
    },
    true
  );

  document.addEventListener('click', onCartChoice);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // Variant switch: swap in the server-rendered state for the new variant.
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data || !event.data.html) return;
      var id = 'NoSingleRemainder-' + event.data.sectionId;
      var current = document.getElementById(id);
      var updated = event.data.html.getElementById(id);
      if (current) rules.delete(current);
      if (current && updated) current.replaceWith(updated);
      else if (current) current.remove();
      else if (updated) {
        var rulesEl = document.getElementById('Quantity-Rules-' + event.data.sectionId);
        if (rulesEl) rulesEl.after(updated);
      }
      init(document);
    });
    // Stepper bounds reset (variant/cart change): re-evaluate.
    subscribe(PUB_SUB_EVENTS.quantityUpdate, function () {
      rules.forEach(function (rule) {
        if (rule.input) {
          rule.applyDefaultQuantity();
          rule.update();
        }
      });
    });
    // Cart page re-renders its lines after each change.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      init(document);
    });
  }

  window.NoSingleRemainder = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
